import React, { useState, useRef, useCallback } from "react";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import ComponentSuspense from "../../../../../../Common/ComponentSuspense";
import MultipleChipInput from "../../../../../../Common/MultipleChipInput";
import { ButtonUI } from "../../../../../../Common";

import { setPrevState } from "../../../../../../../util/common";
import { regexBasicEmail } from "../../../../../../../util/regularExpress";

const KMKTextEditor = React.lazy(() => import("../../../../../../Common/KMKTextEditor"));

const EmailContent = (props) => {
  const { jobState, setJobState } = props;

  const emailBodyref = useRef(null);

  const [inputValues, setInputValues] = useState("");

  const handleClear = useCallback(() => {
    setPrevState(setJobState, { emails: [] });
    setInputValues("");
  }, [setJobState]);

  const handleEmailChange = useCallback(
    (newValue) => {
      const lastEmail = newValue[newValue.length - 1];
      if (regexBasicEmail(lastEmail)) setPrevState(setJobState, { emails: newValue });
      else setInputValues(lastEmail || "");
    },
    [setJobState]
  );

  const onDelete = useCallback(
    (option) => setPrevState(setJobState, { emails: jobState.emails.filter((email) => email !== option) }),
    [jobState.emails, setJobState]
  );

  const processEmails = useCallback(
    (emailString) => {
      const emailArray = emailString.split(",").map((email) => email.trim());

      let newEmails = [...jobState.emails];
      emailArray.forEach((email) => {
        if (regexBasicEmail(email) && !newEmails.includes(email)) newEmails.push(email.toLowerCase());
      });

      setPrevState(setJobState, { emails: newEmails });
      setInputValues("");
    },
    [jobState.emails, setJobState]
  );

  const handleKeyPress = useCallback(
    (event) => {
      const trimmedValue = inputValues?.trim();
      if (
        event.key === "Enter" &&
        jobState.emails?.some((email) => email?.toLowerCase() === trimmedValue?.toLowerCase())
      ) {
        event.preventDefault();
        setInputValues("");
      }
      if (event.key === ",") {
        event.preventDefault();
        if (trimmedValue) processEmails(trimmedValue);
      }
    },
    [inputValues, jobState.emails, processEmails]
  );

  const handlePaste = useCallback(
    (event) => {
      const pastedData = event.clipboardData.getData("text");
      event.preventDefault();
      if (pastedData) processEmails(pastedData);
    },
    [processEmails]
  );

  return (
    <Box sx={{ my: 2 }}>
      <TextField
        value={jobState.subject}
        sx={{ width: "100%", mb: 2 }}
        required
        label="Subject"
        onChange={(e) => setPrevState(setJobState, { subject: e.target.value })}
        placeholder="Here is your requested report"
      />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
        <MultipleChipInput
          placeholder="Enter Email Recipients"
          value={jobState.emails ?? []}
          inputValue={inputValues ?? ""}
          helperText="Press Enter or use commas to add email addresses separately."
          handleonInputChange={(event, newInputValue) => setInputValues(newInputValue)}
          handleOnChange={(event, newValue) => handleEmailChange(newValue)}
          onDelete={(option) => onDelete(option)}
          handleKeyPress={(e) => handleKeyPress(e)}
          handlePaste={(e) => handlePaste(e)}
          disableClearable={true}
          maxHeight="380px"
        />
        <ButtonUI
          variant="text"
          color="secondary"
          style={{ margin: "0px 10px 7px 0", fontSize: "14px", minWidth: "100px" }}
          onMouseDown={(e) => handleClear()}
          disabled={!jobState.emails?.length}
        >
          Clear
        </ButtonUI>
      </Box>

      <Box sx={{ marginBottom: "50px" }}>
        <ComponentSuspense>
          <KMKTextEditor
            editorRef={emailBodyref}
            placeholder="Hi, this is an alert regarding..."
            value={jobState.body}
            setValue={(content) => {
              setPrevState(setJobState, { body: content });
            }}
            style={{ height: "250px", width: "100%" }}
            theme="snow"
          />
        </ComponentSuspense>
      </Box>
    </Box>
  );
};

export default EmailContent;
